import { isEmptyString } from "../../common/typeguards";
import type { RemoteApiSpec, RemoteParameter } from "../../generated-proto/pb_schema/camera_kit/v3/remote_api_spec";
import { RemoteEndpoint_HttpRequestMethod } from "../../generated-proto/pb_schema/camera_kit/v3/remote_api_spec";
import { RemoteParameter_ParameterLocation } from "../../generated-proto/pb_schema/camera_kit/v3/remote_api_spec";
import { namedError } from "../../namedErrors";
import type { ValidationStrategy } from "../../configuration";
import type { UriRequest } from "../UriHandlers";

export const requestValidationErrorName = "RequestValidationError";
export const requestValidationError = namedError(requestValidationErrorName);

export function validateRequest(
    request: UriRequest,
    specs: RemoteApiSpec[],
    validationStrategy: ValidationStrategy = "strict"
) {
    // Deny all requests if strategy is "deny"
    if (validationStrategy === "deny") {
        throw requestValidationError("All lens HTTP requests are denied by the current validation strategy.");
    }

    // Skip all validation if unrestricted
    if (validationStrategy === "unrestricted") {
        return;
    }

    const url = new URL(request.uri);

    // 1. Given url, method and headers and spec:
    for (const spec of specs) {
        // 2. Check protocol and host.
        if (url.host !== spec.host || url.protocol !== (spec.tlsRequired ? "https:" : "http:")) {
            continue;
        }

        // If validationStrategy is "host", we only validate the host, so we're done
        if (validationStrategy === "host") {
            return;
        }

        // 3. Go for each part together with the path parameters.
        const path = url.pathname.replace(/^\/|\/$/g, "");
        for (const endpoint of spec.endpoints) {
            const endpointPath = endpoint.path.replace(/^\/|\/$/g, "");
            if (!path.startsWith(endpointPath)) continue;

            const method =
                RemoteEndpoint_HttpRequestMethod[request.method] ?? RemoteEndpoint_HttpRequestMethod.UNRECOGNIZED;
            if (!endpoint.methods.includes(method)) continue;

            // Validate path parameters (they are part of the route)
            validatePath(path.split(endpointPath)[1], endpoint.parameters);

            // If validationStrategy is "route", we validate host + path + method + path parameters
            // but skip query and header validation
            if (validationStrategy === "route") {
                return;
            }

            // For "strict" validation, also validate query parameters and headers
            validateHeaders(request.metadata, endpoint.parameters);
            validateQuery(url.searchParams, endpoint.parameters);
            // The request matches the current endpoint
            return;
        }
    }

    // The request does not match any of the endpoints
    throw requestValidationError("The request does not match any of the Remote API specifications.");
}

export function validatePath(path: string, parameters: RemoteParameter[]) {
    const pathComponents = path.split("/").filter(Boolean);

    let paramIndex = 0;

    for (const param of parameters) {
        if (param.location !== RemoteParameter_ParameterLocation.PATH) continue;

        const paramNameComponent = pathComponents[paramIndex];
        const paramValueComponent = pathComponents[paramIndex + 1];

        if (param.constant) {
            // For constant parameters, the value must match the defaultValue
            if (paramNameComponent === param.name && paramValueComponent === param.defaultValue) {
                paramIndex += 2; // Move to the next parameter pair
            } else {
                throw requestValidationError(
                    `Expected constant parameter '${param.name}' with value '${param.defaultValue}' ` +
                        `at position ${paramIndex}, but found '${paramNameComponent}' and '${paramValueComponent}'.`
                );
            }
        } else if (param.optional) {
            if (paramNameComponent === param.name) {
                // Check if there's another component for its value
                if (paramValueComponent !== undefined) {
                    // Optional param with a value
                    paramIndex += 2;
                } else {
                    // Optional param without a value
                    paramIndex += 1;
                }
            }
            // If optional and not present, continue without incrementing paramIndex
        } else {
            // For regular parameters, we just need to ensure a name/value pair exists
            if (paramNameComponent === param.name && paramValueComponent !== undefined) {
                paramIndex += 2; // Move to the next parameter pair
            } else {
                throw requestValidationError(
                    `Expected parameter '${param.name}' with a value at position ${paramIndex}, ` +
                        `but found '${paramNameComponent}' and '${paramValueComponent}'.`
                );
            }
        }
    }

    // After processing all parameters, there should be no extra path components
    if (paramIndex !== pathComponents.length) {
        const invalidPath = pathComponents.slice(paramIndex).join("/");
        throw requestValidationError(
            `Unexpected extra path components starting at position ${paramIndex}: '${invalidPath}'.`
        );
    }
}

export function validateHeaders(headers: Record<string, string>, parameters: RemoteParameter[]) {
    for (const param of parameters) {
        if (param.location !== RemoteParameter_ParameterLocation.HEADER) continue;

        const headerValue = headers[param.name];

        if (param.constant) {
            // For constant parameters, the header must exist and match defaultValue
            if (headerValue !== param.defaultValue) {
                throw requestValidationError(
                    `Expected constant header '${param.name}' with value '${param.defaultValue}', ` +
                        `but found '${headerValue ?? "undefined"}'.`
                );
            }
        } else if (param.optional) {
            // If optional, missing or present value is OK
        } else {
            // For required parameters, the header must exist and have a non-empty value
            if (headerValue == undefined || isEmptyString(headerValue)) {
                throw requestValidationError(`Required header '${param.name}' is missing or empty.`);
            }
        }
    }
}

export function validateQuery(queryParams: URLSearchParams, parameters: RemoteParameter[]) {
    for (const param of parameters) {
        if (param.location !== RemoteParameter_ParameterLocation.QUERY) continue;

        const paramValue = queryParams.get(param.name);

        if (param.constant) {
            // For constant parameters, the query parameter must exist and match defaultValue
            if (paramValue !== param.defaultValue) {
                throw requestValidationError(
                    `Expected constant query parameter '${param.name}' with value '${param.defaultValue}', ` +
                        `but found '${paramValue ?? "undefined"}'.`
                );
            }
        } else if (param.optional) {
            // If optional, missing or present value is OK
        } else {
            // For required parameters, the query parameter must exist and have a non-empty value
            if (paramValue == undefined || isEmptyString(paramValue)) {
                throw requestValidationError(`Required query parameter '${param.name}' is missing or empty.`);
            }
        }
    }
    // All query parameters are valid
}
